import React, { useState, useEffect, useRef } from 'react';
import { useToast } from '../ui/use-toast';
import { Button } from '../ui/button';
import { Mic, MicOff, Settings, Play, Square, Volume2 } from 'lucide-react';
import { Agent } from '../../types/agent-types-new';
import { AssistantState, MessageType } from './voice-types';
import { agentService } from '../../lib/api/agent-service';

// Voice Types
export enum VoiceType {
  OpenAI = 'openai',
  ElevenLabs = 'elevenlabs',
}

// TTS Voice Options
export const OpenAIVoices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
export const LanguageOptions = ['en', 'es', 'fr', 'de', 'it', 'pt', 'ja', 'zh', 'ru'];

interface VoiceAssistantTesterProps {
  agent: Agent | null | undefined;
}

const VoiceAssistantTester: React.FC<VoiceAssistantTesterProps> = ({ agent }) => {
  const { toast } = useToast();
  const [connected, setConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [currentState, setCurrentState] = useState<AssistantState>(AssistantState.IDLE);
  const [wsConnection, setWsConnection] = useState<WebSocket | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [transcription, setTranscription] = useState<string>('');
  const [assistantMessage, setAssistantMessage] = useState<string>('');
  const [conversation, setConversation] = useState<{role: 'user' | 'assistant', content: string}[]>([]);
  
  // Audio processing refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioProcessorRef = useRef<ScriptProcessorNode | null>(null);
  
  // Audio visualization
  const visualizerRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  
  // Settings
  const [voiceType, setVoiceType] = useState<VoiceType>(VoiceType.OpenAI);
  const [selectedVoice, setSelectedVoice] = useState<string>('nova');
  const [language, setLanguage] = useState<string>('en');
  const [showSettings, setShowSettings] = useState(false);
  
  // WebSocket server URL - will be set after fetching
  const [wsServerUrl, setWsServerUrl] = useState<string>('');

  // Connect to WebSocket
  const connectWebSocket = () => {
    if (wsConnection) {
      wsConnection.close();
    }
    
    try {
      const ws = new WebSocket(wsServerUrl);
      ws.binaryType = 'arraybuffer'; // Set binary type for audio data
      
      ws.onopen = () => {
        console.log('WebSocket connection established');
        setConnected(true);
        
        // Initialize session
        ws.send(JSON.stringify({
          type: 'initialize_session'
        }));
      };
      
      ws.onclose = () => {
        console.log('WebSocket connection closed');
        setConnected(false);
        setSessionId(null);
        
        // Try to reconnect after a short delay
        setTimeout(() => {
          if (!connected) {
            connectWebSocket();
          }
        }, 3000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        toast({
          title: "Connection Error",
          description: "Failed to connect to the voice assistant server",
          variant: "destructive",
        });
        setConnected(false);
      };
      
      ws.onmessage = (event) => {
        handleWebSocketMessage(event);
      };
      
      setWsConnection(ws);
    } catch (error) {
      console.error('Error connecting to WebSocket:', error);
      toast({
        title: "Connection Error",
        description: "Failed to connect to the voice assistant server",
        variant: "destructive",
      });
    }
  };
  
  // Handle WebSocket messages
  const handleWebSocketMessage = (event: MessageEvent) => {
    // Handle binary data (audio chunks)
    if (event.data instanceof ArrayBuffer) {
      handleAudioData(event.data);
      return;
    }
    
    // Handle JSON messages
    try {
      const message = JSON.parse(event.data);
      
      switch (message.type) {
        case 'session_initialized':
          setSessionId(message.sessionId);
          console.log('Session initialized:', message.sessionId);
          break;
          
        case MessageType.STATE_CHANGE:
          handleStateChange(message.state);
          break;
          
        case MessageType.TRANSCRIPTION:
        case MessageType.TRANSCRIPTION_FINAL:
          handleTranscription(message.text, message.type === MessageType.TRANSCRIPTION_FINAL);
          break;
          
        case MessageType.ASSISTANT_MESSAGE:
        case MessageType.ASSISTANT_MESSAGE_CHUNK:
          handleAssistantMessage(message.text, message.type === MessageType.ASSISTANT_MESSAGE);
          break;
          
        case MessageType.ERROR:
          handleError(message.error);
          break;
          
        default:
          console.log('Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  };
  
  // Handle audio data
  const handleAudioData = (data: ArrayBuffer) => {
    // Process audio data (play it)
    // In a real implementation, this would stream the audio to an audio element
    console.log('Received audio data:', data.byteLength, 'bytes');
    
    // This is where you would use MediaSource Extensions to play the audio
    // For simplicity, we'll just log it
  };
  
  // Handle state change
  const handleStateChange = (state: AssistantState) => {
    console.log('State changed to:', state);
    setCurrentState(state);
    
    if (state === AssistantState.LISTENING) {
      setIsListening(true);
    } else {
      setIsListening(false);
    }
  };
  
  // Handle transcription
  const handleTranscription = (text: string, isFinal: boolean) => {
    setTranscription(text);
    
    if (isFinal) {
      // Add to conversation
      const updatedConversation = [...conversation, { role: 'user' as const, content: text }];
      setConversation(updatedConversation);
    }
  };
  
  // Handle assistant message
  const handleAssistantMessage = (text: string, isFinal: boolean) => {
    setAssistantMessage(text);
    
    if (isFinal) {
      // Add to conversation
      const updatedConversation = [...conversation, { role: 'assistant' as const, content: text }];
      setConversation(updatedConversation);
    }
  };
  
  // Handle errors
  const handleError = (error: string) => {
    console.error('Voice assistant error:', error);
    toast({
      title: "Error",
      description: error,
      variant: "destructive",
    });
  };
  
  // Toggle listening
  const toggleListening = () => {
    if (!wsConnection || !sessionId) return;
    
    if (isListening) {
      // Stop listening
      wsConnection.send(JSON.stringify({
        type: MessageType.STOP_LISTENING,
        sessionId
      }));
    } else {
      // Request microphone access if needed
      if (!mediaStreamRef.current) {
        initializeMicrophone();
        return;
      }
      
      // Start listening
      wsConnection.send(JSON.stringify({
        type: MessageType.START_LISTENING,
        sessionId
      }));
    }
  };
  
  // Interrupt the assistant
  const handleInterrupt = () => {
    if (!wsConnection || !sessionId) return;
    
    wsConnection.send(JSON.stringify({
      type: MessageType.INTERRUPT,
      sessionId
    }));
  };
  
  // Initialize microphone
  const initializeMicrophone = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      // Create audio context
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const analyzer = audioContext.createAnalyser();
      analyzer.fftSize = 256;
      
      // Create microphone input
      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyzer);
      
      // Create script processor for audio processing
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      analyzer.connect(processor);
      processor.connect(audioContext.destination);
      
      // Store references
      audioContextRef.current = audioContext;
      mediaStreamRef.current = stream;
      analyzerRef.current = analyzer;
      microphoneRef.current = microphone;
      audioProcessorRef.current = processor;
      
      // Set up audio processing
      processor.onaudioprocess = (event) => {
        if (isListening && wsConnection && sessionId) {
          const inputData = event.inputBuffer.getChannelData(0);
          sendAudioData(inputData);
        }
      };
      
      // Start visualization
      startVisualization();
      
      // Start listening
      if (wsConnection && sessionId) {
        wsConnection.send(JSON.stringify({
          type: MessageType.START_LISTENING,
          sessionId
        }));
      }
      
      toast({
        title: "Microphone Access",
        description: "Microphone access granted successfully",
      });
    } catch (error) {
      console.error('Error initializing microphone:', error);
      toast({
        title: "Microphone Error",
        description: "Failed to access microphone. Please check permissions.",
        variant: "destructive",
      });
    }
  };
  
  // Start audio visualization
  const startVisualization = () => {
    if (!analyzerRef.current || !visualizerRef.current) return;
    
    const analyzer = analyzerRef.current;
    const canvas = visualizerRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return;
    
    // Set up visualization
    const bufferLength = analyzer.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    // Animation function
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      
      // Get frequency data
      analyzer.getByteFrequencyData(dataArray);
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw visualization
      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height;
        
        // Use different colors based on state
        ctx.fillStyle = currentState === AssistantState.LISTENING 
          ? 'rgba(245, 0, 87, 0.8)' // Listening state (pink)
          : currentState === AssistantState.SPEAKING 
            ? 'rgba(76, 175, 80, 0.8)' // Speaking state (green)
            : 'rgba(74, 111, 255, 0.8)'; // Default (blue)
        
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        
        x += barWidth + 1;
      }
    };
    
    // Start animation
    animate();
  };
  
  // Send audio data to server
  const sendAudioData = (audioData: Float32Array) => {
    if (!wsConnection || !sessionId) return;
    
    // Convert to 16-bit PCM
    const pcmData = convertFloatTo16Bit(audioData);
    
    // Convert to base64
    const base64Data = arrayBufferToBase64(pcmData.buffer);
    
    // Send to server
    wsConnection.send(JSON.stringify({
      type: MessageType.AUDIO_DATA,
      sessionId,
      data: base64Data,
      isFinal: false
    }));
  };
  
  // Convert float audio data to 16-bit PCM
  const convertFloatTo16Bit = (floatData: Float32Array): Int16Array => {
    const pcmData = new Int16Array(floatData.length);
    
    for (let i = 0; i < floatData.length; i++) {
      // Convert to 16-bit signed integer
      const s = Math.max(-1, Math.min(1, floatData[i]));
      pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    
    return pcmData;
  };
  
  // Convert array buffer to base64
  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    
    return window.btoa(binary);
  };
  
  // Apply voice settings
  const applySettings = () => {
    if (!wsConnection || !sessionId) return;
    
    // Send settings to server
    wsConnection.send(JSON.stringify({
      type: 'update_settings',
      sessionId,
      settings: {
        voiceType,
        voice: selectedVoice,
        language
      }
    }));
    
    setShowSettings(false);
    
    toast({
      title: "Settings Updated",
      description: "Voice assistant settings have been updated",
    });
  };
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Close WebSocket connection
      if (wsConnection) {
        wsConnection.close();
      }
      
      // Stop microphone
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // Close audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      
      // Cancel animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);
  
  // Fetch WebSocket URL and connect when component mounts
  useEffect(() => {
    if (agent && agentService.hasVoiceCapabilities(agent)) {
      // Fetch WebSocket URL first
      const fetchWsUrl = async () => {
        try {
          const url = await agentService.getVoiceAssistantWsUrl();
          setWsServerUrl(url);
          
          // Check service status
          const status = await agentService.getVoiceAssistantStatus();
          if (status.status !== 'online') {
            toast({
              title: "Service Status",
              description: status.message,
              variant: status.status === 'degraded' ? 'default' : 'destructive',
            });
          }
        } catch (error) {
          console.error('Error fetching WebSocket URL:', error);
          // Set default value as fallback - use relative URL to work on any domain
          const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          const host = window.location.host;
          setWsServerUrl(`${protocol}//${host}/ws/voice-assistant`);
        }
      };
      
      fetchWsUrl();
    }
  }, [agent]);
  
  // Connect when WebSocket URL is available
  useEffect(() => {
    if (wsServerUrl && agent && agentService.hasVoiceCapabilities(agent)) {
      connectWebSocket();
    }
  }, [wsServerUrl, agent]);
  
  // Get status label
  const getStatusLabel = () => {
    switch (currentState) {
      case AssistantState.IDLE:
        return 'Ready';
      case AssistantState.LISTENING:
        return 'Listening...';
      case AssistantState.PROCESSING:
        return 'Processing...';
      case AssistantState.SPEAKING:
        return 'Speaking...';
      case AssistantState.INTERRUPTED:
        return 'Interrupted';
      default:
        return 'Not connected';
    }
  };
  
  // Status indicator color
  const getStatusColor = () => {
    switch (currentState) {
      case AssistantState.IDLE:
        return 'bg-gray-400';
      case AssistantState.LISTENING:
        return 'bg-pink-500';
      case AssistantState.PROCESSING:
        return 'bg-blue-500';
      case AssistantState.SPEAKING:
        return 'bg-green-500';
      case AssistantState.INTERRUPTED:
        return 'bg-amber-500';
      default:
        return 'bg-gray-300';
    }
  };
  
  return (
    <div className="flex flex-col">
      {/* Status Bar */}
      <div className="rounded-t-lg bg-gray-100 p-3 flex justify-between items-center border-b">
        <div className="flex items-center">
          <div className={`w-3 h-3 rounded-full ${getStatusColor()} mr-2`}></div>
          <span className="text-sm font-medium">{getStatusLabel()}</span>
        </div>
        
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => setShowSettings(!showSettings)}
          >
            <Settings className="h-4 w-4" />
          </Button>
          
          <Button 
            size="sm" 
            variant="outline" 
            onClick={connectWebSocket}
            disabled={connected}
          >
            <Play className="h-4 w-4 mr-1" />
            Connect
          </Button>
        </div>
      </div>
      
      {/* Settings Panel */}
      {showSettings && (
        <div className="p-4 border-b bg-gray-50">
          <h3 className="text-sm font-medium mb-3">Voice Settings</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Voice Provider</label>
              <select 
                value={voiceType}
                onChange={(e) => setVoiceType(e.target.value as VoiceType)}
                className="w-full p-2 text-sm border rounded-md"
              >
                <option value={VoiceType.OpenAI}>OpenAI</option>
                <option value={VoiceType.ElevenLabs}>ElevenLabs</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs text-gray-500 mb-1">Voice</label>
              <select 
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
                className="w-full p-2 text-sm border rounded-md"
              >
                {voiceType === VoiceType.OpenAI ? (
                  OpenAIVoices.map(voice => (
                    <option key={voice} value={voice}>{voice}</option>
                  ))
                ) : (
                  <option value="eleven_monolingual_v1">Default Voice</option>
                )}
              </select>
            </div>
            
            <div>
              <label className="block text-xs text-gray-500 mb-1">Language</label>
              <select 
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full p-2 text-sm border rounded-md"
              >
                {LanguageOptions.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button 
              size="sm" 
              onClick={applySettings}
              disabled={!connected}
            >
              Apply Settings
            </Button>
          </div>
        </div>
      )}
      
      {/* Visualization Area */}
      <div className="p-4 border-b">
        <canvas 
          ref={visualizerRef} 
          className="w-full h-32 bg-gray-50 rounded-md border" 
          width={600} 
          height={128}
        ></canvas>
        
        {transcription && (
          <div className="mt-3 text-center">
            <p className="text-sm bg-gray-50 p-2 rounded-md inline-block">
              {transcription}
            </p>
          </div>
        )}
      </div>
      
      {/* Conversation Area */}
      <div className="flex-grow p-4 max-h-64 overflow-y-auto border-b">
        <div className="space-y-4">
          {conversation.map((message, index) => (
            <div 
              key={index} 
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div 
                className={`max-w-[80%] rounded-lg p-3 ${
                  message.role === 'user' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {message.content}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Control Area */}
      <div className="p-4 flex justify-center gap-3">
        <Button 
          size="lg" 
          className={`rounded-full w-16 h-16 flex items-center justify-center ${
            isListening ? 'bg-pink-500 hover:bg-pink-600' : ''
          }`}
          onClick={toggleListening}
          disabled={!connected}
        >
          {isListening ? (
            <MicOff className="h-6 w-6" />
          ) : (
            <Mic className="h-6 w-6" />
          )}
        </Button>
        
        {currentState === AssistantState.SPEAKING && (
          <Button 
            size="lg" 
            variant="outline" 
            className="rounded-full w-12 h-12 flex items-center justify-center"
            onClick={handleInterrupt}
          >
            <Square className="h-5 w-5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default VoiceAssistantTester;